import React, { useState, useRef, useEffect } from 'react';
import Motion from '@/client/components/ui/Motion';
import Badge from '@/client/components/ui/Badge';
import Button from '@/client/components/ui/Button';
import Input from '@/client/components/ui/Input';
import Separator from '@/client/components/ui/Separator';
import Tooltip from '@/client/components/ui/Tooltip';
import { AlertTriangle, BarChart3, MessageCircle, X, Send, User, HelpCircle, Plug } from 'lucide-preact';
import CrossPathLogo from '@/client/assets/identity/logo.svg';
import useContext from '@/client/context';
import { OPEN_CHAT_EVENT } from '@/client/catalogs/support';
import { errorMessages } from '@/common/libs/sales/error';

/** Outreach channels whose disconnection silently stalls campaign steps. */
const CHECKED_CONNECTIONS = [
    { key: 'linkedin', label: 'LinkedIn' },
    { key: 'whatsapp', label: 'WhatsApp' },
    { key: 'gmail', label: 'Gmail' },
] as const;

type ChatbotIssue = { error: string | null; contactLead?: { id: string } };
type ChatbotCampaign = {
    status: string;
    stats?: { leads?: number; sentMessages?: number; replies?: number };
};

const friendlyIssueReason = (error: string | null): string => {
    if (!error) return 'Unknown error';
    if (error === 'run_interrupted') return 'Interrupted mid-send';
    return errorMessages[error as keyof typeof errorMessages]?.short ?? error;
};

interface Message {
    id: string;
    text: string;
    sender: 'user' | 'bot';
    timestamp: Date;
    type?: 'text' | 'instruction' | 'help';
}

interface ChatbotProps {
    className?: string;
    currentPage?: string;
}

const Chatbot: React.FC<ChatbotProps> = ({ className = '', currentPage = 'dashboard' }) => {
    const { Prospect, Integrations } = useContext();
    const [isOpen, setIsOpen] = useState(false);

    // The widget lives in the layout, so other pages (Help & Support) ask for it via an event
    // instead of needing a shared store just to open the panel.
    useEffect(() => {
        const openChat = () => setIsOpen(true);
        window.addEventListener(OPEN_CHAT_EVENT, openChat);
        return () => window.removeEventListener(OPEN_CHAT_EVENT, openChat);
    }, []);
    const getWelcomeMessage = (): string => {
        switch (currentPage) {
            case 'style':
                return "Hello! I'm here to help you configure your communication style. You can ask me about writing styles, tones, approaches, or how to use the tooltips for detailed explanations.";
            case 'services':
                return 'Hi! I can help you set up your service offerings. Ask me about mission types, billing rules, work modes, or any other service configuration questions.';
            case 'recruitment-missions':
                return 'Welcome! I can help you navigate available recruitment missions, explain how to apply, or assist with filtering and finding the right opportunities.';
            case 'campaign':
                return "Hello! I'm here to help with your campaign management. Ask me about creating campaigns, tracking performance, or optimizing your outreach strategies.";
            case 'filter':
                return 'Hi! I can help you create and manage filters, explain advanced search criteria, or assist with saving and sharing filter combinations.';
            case 'pipeline':
                return 'Welcome! I can help you manage your candidate pipeline, explain how to move candidates between stages, or assist with tracking and reporting.';
            case 'talent-pool':
                return "Hello! I'm here to help you manage your talent pool. Ask me about organizing candidates, importing data, or setting up matching recommendations.";
            case 'meetings':
                return 'Hi! I can help you schedule meetings, set up reminders, or assist with meeting management and follow-up tracking.';
            case 'portal-link':
                return 'Welcome! I can help you create portal links, manage client access, or assist with secure document sharing and tracking.';
            case 'collaboration':
                return "Hello! I'm here to help with team collaboration features. Ask me about shared projects, workflow coordination, or team performance tracking.";
            case 'account-details':
                return 'Hi! I can help you manage your account settings, update your profile, or assist with subscription and billing questions.';
            default:
                return "Hello! I'm your CrossPath assistant. How can I help you today?";
        }
    };

    const [messages, setMessages] = useState<Message[]>([
        { id: '1', text: getWelcomeMessage(), sender: 'bot', timestamp: new Date(), type: 'text' },
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const quickActions = [
        { icon: AlertTriangle, label: 'Why did my campaigns stop?', action: 'failed-leads' },
        { icon: BarChart3, label: 'How are my campaigns doing?', action: 'summary' },
        { icon: Plug, label: 'Are my channels connected?', action: 'connections' },
        { icon: HelpCircle, label: 'How to use this page?', action: 'help' },
    ];

    const quickActionStyles = [
        'border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-950 hover:shadow-sm',
        'border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-950 hover:shadow-sm',
        'border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-950 hover:shadow-sm',
        'border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-950 hover:shadow-sm',
    ];

    // Surface a stalled account the moment the panel opens, instead of waiting to be asked —
    // a disconnected channel silently stops every campaign step on it.
    const healthCheckedRef = useRef(false);
    useEffect(() => {
        if (!isOpen || healthCheckedRef.current) return;
        healthCheckedRef.current = true;

        (async () => {
            try {
                const [status, issues] = await Promise.all([
                    Integrations.getStatus() as Promise<Record<string, boolean> | undefined>,
                    Prospect.Campaign.getIssues({}) as Promise<ChatbotIssue[]>,
                ]);
                const disconnected = CHECKED_CONNECTIONS.filter((connection) => !status?.[connection.key]);
                const stuckLeads = new Set((issues ?? []).map((issue) => issue.contactLead?.id).filter(Boolean)).size;
                if (!disconnected.length && !stuckLeads) return;

                pushBotMessage(
                    [
                        'Heads up on your account:',
                        stuckLeads ? `• ${stuckLeads} lead${stuckLeads === 1 ? '' : 's'} stuck on a failed step` : '',
                        disconnected.length
                            ? `• Disconnected: ${disconnected.map((connection) => connection.label).join(', ')}`
                            : '',
                        '',
                        'Use the buttons below and I can break it down for you.',
                    ]
                        .filter(Boolean)
                        .join('\n'),
                );
            } catch (error) {
                console.error('Chatbot health check failed', error);
            }
        })();
    }, [isOpen]);

    const getPageSpecificHelp = (): string => {
        switch (currentPage) {
            case 'style':
                return 'Style page help:\n\n• Select your preferred writing styles (multiple selections allowed)\n• Choose your communication tone and approach\n• Set your message structure and commercial posture\n• Add examples to teach the AI your style\n• Use the tooltips (hover over info icons) for detailed explanations';
            case 'services':
                return 'Services page help:\n\n• Configure your mission types and billing rules\n• Set your work mode preferences\n• Choose search methods (max 2 selections)\n• Define guarantees and additional services\n• Add custom details in the text area';
            case 'recruitment-missions':
                return 'Recruitment Missions help:\n\n• Browse available recruitment missions from CrossPath\n• Use filters to find missions matching your criteria\n• View mission details and requirements\n• Apply to missions that interest you\n• Track your applications and progress';
            case 'campaign':
                return 'Campaign help:\n\n• Create and manage outreach campaigns\n• Set campaign objectives and target audiences\n• Track campaign performance and metrics\n• Schedule and automate campaign activities\n• Analyze results and optimize strategies';
            case 'filter':
                return 'Filter help:\n\n• Create custom filters to narrow down results\n• Save frequently used filter combinations\n• Apply filters to search results\n• Share filters with team members\n• Use advanced criteria for precise matching';
            case 'pipeline':
                return 'Pipeline help:\n\n• Track candidates through different stages\n• Move candidates between pipeline stages\n• Add notes and updates for each candidate\n• Set follow-up reminders and tasks\n• Generate pipeline reports and analytics';
            case 'talent-pool':
                return 'Talent Pool help:\n\n• Manage your saved candidates\n• Organize candidates by categories or tags\n• Import candidates from external sources\n• Export candidate data for external use\n• Set up candidate matching and recommendations';
            case 'meetings':
                return 'Meetings help:\n\n• Schedule client meetings and appointments\n• Set up meeting reminders and notifications\n• Track meeting outcomes and follow-ups\n• Integrate with calendar applications\n• Generate meeting reports and summaries';
            case 'portal-link':
                return 'Portal Links help:\n\n• Create secure portal links for clients\n• Share documents and information securely\n• Track portal usage and engagement\n• Set access permissions and expiration dates\n• Monitor client activity and interactions';
            case 'collaboration':
                return 'Collaboration help:\n\n• Work with team members on shared projects\n• Share candidate information and notes\n• Coordinate outreach activities\n• Set up collaborative workflows\n• Track team performance and contributions';
            case 'account-details':
                return 'Account Details help:\n\n• Update your personal and company information\n• Manage your subscription and billing\n• Set up security preferences\n• Configure notification settings\n• Access account analytics and usage data';
            default:
                return 'Dashboard help:\n\n• Use the sidebar to navigate between different sections\n• View your key metrics and performance indicators\n• Access quick actions and shortcuts\n• Monitor your recent activity and updates\n• Get personalized recommendations and insights';
        }
    };

    const getBotResponse = (userMessage: string, action?: string): string => {
        const message = userMessage.toLowerCase();

        if (action === 'help') {
            return getPageSpecificHelp();
        }

        if (action === 'instructions') {
            return 'Here are some key instructions:\n\n• Use the sidebar to navigate between different sections\n• Click on items to view details\n• Use filters to narrow down results\n• Save your preferences in Settings\n• Contact support if you need additional help';
        }

        if (action === 'tips') {
            return '💡 Pro Tips:\n\n• Keep your profile updated for better matches\n• Use the search filters to find exactly what you need\n• Save frequently used searches\n• Set up notifications for important updates\n• Use keyboard shortcuts for faster navigation';
        }

        if (action === 'settings') {
            return 'Settings help:\n\n• Go to Settings > Account Details to update your profile\n• Use Settings > Style to customize your communication preferences\n• Configure your services in Settings > Services\n• Adjust notification preferences in Settings > Communication';
        }

        // General responses based on keywords
        if (message.includes('hello') || message.includes('hi')) {
            return "Hello! I'm here to help you with CrossPath. What can I assist you with today?";
        }

        if (message.includes('help') || message.includes('support')) {
            return "I'm here to help! You can ask me about:\n\n• How to use specific features\n• Navigation tips\n• Account settings\n• Troubleshooting issues\n\nWhat specific help do you need?";
        }

        if (message.includes('dashboard') || message.includes('main')) {
            return "The dashboard is your central hub where you can:\n\n• View your active roles and progress\n• Search for candidates\n• Manage your outreach campaigns\n• Access all your tools and features\n\nIs there a specific part of the dashboard you'd like to know more about?";
        }

        // Page-specific responses
        if (currentPage === 'style' && (message.includes('style') || message.includes('communication'))) {
            return 'On the Style page, you can:\n\n• Select multiple writing styles that match your personality\n• Choose your communication tone (formal/informal)\n• Set your approach (sales/consulting)\n• Define your message structure\n• Add examples to teach the AI your unique style\n\nHover over the info icons for detailed explanations of each option.';
        }

        if (currentPage === 'services' && (message.includes('service') || message.includes('mission'))) {
            return 'On the Services page, you can:\n\n• Configure your mission types (permanent, temporary, etc.)\n• Set billing rules and payment terms\n• Choose your work mode preferences\n• Select search methods (max 2 allowed)\n• Define guarantees and additional services\n\nThis helps CrossPath understand your service offerings and match you with relevant opportunities.';
        }

        if (
            currentPage === 'recruitment-missions' &&
            (message.includes('mission') || message.includes('recruitment'))
        ) {
            return 'The Recruitment Missions page shows:\n\n• Available recruitment missions from CrossPath\n• Mission details, requirements, and compensation\n• Filter options to find relevant missions\n• Application status and progress tracking\n• Mission categories and specializations\n\nUse the filters to find missions that match your expertise and interests.';
        }

        if (message.includes('search') || message.includes('find')) {
            return 'To search effectively:\n\n• Use the search bar at the top of pages\n• Apply filters to narrow down results\n• Save your search criteria for future use\n• Use keywords that match your requirements\n\nWhat are you looking for? I can help you refine your search.';
        }

        if (message.includes('candidate') || message.includes('talent')) {
            return 'For candidate management:\n\n• Use the Candidates Search page to find talent\n• Check the Talent Pool for saved candidates\n• Use the Pipeline to track candidate progress\n• Set up filters to find the right matches\n\nWould you like help with a specific aspect of candidate management?';
        }

        if (message.includes('outreach') || message.includes('campaign')) {
            return 'Outreach and campaigns help:\n\n• Create targeted campaigns in the Campaign section\n• Use the Outreach page to manage your messages\n• Configure your communication style in Settings\n• Track campaign performance and results\n\nNeed help setting up a new campaign?';
        }

        if (message.includes('settings') || message.includes('preferences')) {
            return 'Settings and preferences:\n\n• Account Details: Update your profile information\n• Style: Customize your communication preferences\n• Services: Configure your service offerings\n• Communication: Set up notification preferences\n\nWhich settings would you like to adjust?';
        }

        if (message.includes('error') || message.includes('problem') || message.includes('issue')) {
            return "I'm sorry you're experiencing an issue. Here are some troubleshooting steps:\n\n• Try refreshing the page\n• Check your internet connection\n• Clear your browser cache\n• Try using a different browser\n\nIf the problem persists, please contact our support team for further assistance.";
        }

        // Default response
        return (
            'I understand you\'re asking about: "' +
            userMessage +
            "\"\n\nI'm here to help with CrossPath features, navigation, and general questions. Could you be more specific about what you'd like to know?"
        );
    };

    const handleSendMessage = async (text?: string, action?: string) => {
        const messageText = text || inputValue.trim();
        if (!messageText && !action) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            text: messageText,
            sender: 'user',
            timestamp: new Date(),
            type: action ? 'instruction' : 'text',
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputValue('');
        setIsTyping(true);

        // Simulate bot typing delay
        setTimeout(
            () => {
                const botResponse: Message = {
                    id: (Date.now() + 1).toString(),
                    text: getBotResponse(messageText, action),
                    sender: 'bot',
                    timestamp: new Date(),
                    type: 'text',
                };

                setMessages((prev) => [...prev, botResponse]);
                setIsTyping(false);
            },
            1000 + Math.random() * 1000,
        );
    };

    const pushBotMessage = (text: string) => {
        setMessages((prev) => [
            ...prev,
            { id: `${Date.now()}-bot`, text, sender: 'bot', timestamp: new Date(), type: 'text' },
        ]);
    };

    /** Summarise the caller's own failed campaign steps, grouped by cause. */
    const describeFailedLeads = async (): Promise<string> => {
        const issues = (await Prospect.Campaign.getIssues({})) as ChatbotIssue[];
        if (!issues.length) return 'Good news: none of your campaign steps are stuck right now.';

        const leads = new Set(issues.map((issue) => issue.contactLead?.id).filter(Boolean));
        const byReason = new Map<string, number>();
        for (const issue of issues) {
            const reason = friendlyIssueReason(issue.error);
            byReason.set(reason, (byReason.get(reason) ?? 0) + 1);
        }
        const top = [...byReason.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);

        return [
            `${leads.size} of your leads are stuck on a failed step.`,
            '',
            'Most common causes:',
            ...top.map(([reason, count]) => `• ${reason} (${count})`),
            '',
            'Open Campaigns > Failed leads to review and retry them.',
        ].join('\n');
    };

    /** Snapshot of the caller's own campaigns — no admin/global data involved. */
    const describeCampaignSummary = async (): Promise<string> => {
        const campaigns = (await Prospect.Campaign.List({
            view: 'Private',
            target: 'Client',
        })) as ChatbotCampaign[];
        if (!campaigns.length) return "You don't have any campaigns yet.";

        const active = campaigns.filter((campaign) => campaign.status === 'Active').length;
        const recipients = campaigns.reduce((sum, campaign) => sum + (campaign.stats?.leads ?? 0), 0);
        const sent = campaigns.reduce((sum, campaign) => sum + (campaign.stats?.sentMessages ?? 0), 0);
        const replies = campaigns.reduce((sum, campaign) => sum + (campaign.stats?.replies ?? 0), 0);
        const replyRate = sent > 0 ? ((replies / sent) * 100).toFixed(1) : '0.0';

        return [
            `You have ${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}, ${active} currently running.`,
            '',
            `• People in outreach: ${recipients}`,
            `• Messages sent: ${sent}`,
            `• Replies: ${replies} (${replyRate}%)`,
            campaigns.length > active ? `• Paused: ${campaigns.length - active}` : '',
        ]
            .filter(Boolean)
            .join('\n');
    };

    /** Disconnected channels are the single most common reason campaigns stall. */
    const describeConnections = async (): Promise<string> => {
        const status = (await Integrations.getStatus()) as Record<string, boolean> | undefined;
        if (!status) return 'Unable to read your connection status right now.';

        const disconnected = CHECKED_CONNECTIONS.filter((connection) => !status[connection.key]);
        if (!disconnected.length) return 'All of your outreach channels are connected.';

        return [
            `${disconnected.length} channel${disconnected.length === 1 ? ' is' : 's are'} disconnected:`,
            ...disconnected.map((connection) => `• ${connection.label}`),
            '',
            'Campaign steps on those channels will fail until you reconnect them in Settings > Integrations.',
        ].join('\n');
    };

    const DATA_ACTIONS: Record<string, { question: string; run: () => Promise<string> }> = {
        'failed-leads': { question: 'Why did my campaigns stop?', run: describeFailedLeads },
        summary: { question: 'How are my campaigns doing?', run: describeCampaignSummary },
        connections: { question: 'Are my channels connected?', run: describeConnections },
    };

    const handleQuickAction = async (action: string) => {
        const dataAction = DATA_ACTIONS[action];
        if (!dataAction) {
            handleSendMessage('', action);
            return;
        }

        setMessages((prev) => [
            ...prev,
            {
                id: `${Date.now()}-user`,
                text: dataAction.question,
                sender: 'user',
                timestamp: new Date(),
                type: 'instruction',
            },
        ]);
        setIsTyping(true);
        try {
            pushBotMessage(await dataAction.run());
        } catch (error) {
            console.error('Chatbot data action failed', error);
            pushBotMessage("Sorry, I couldn't load that just now. Please try again in a moment.");
        } finally {
            setIsTyping(false);
        }
    };

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <>
            <Motion
                tag="div"
                className={`fixed bottom-6 right-6 z-50 ${className}`}
                whileHover={{ scale: 1.06, y: -2 }}
                whileTap={{ scale: 0.96 }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5 }}
            >
                <Tooltip content={isOpen ? 'Close assistant' : 'Open CrossPath Assistant'} side="left">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsOpen(!isOpen)}
                        aria-label={isOpen ? 'Close CrossPath Assistant' : 'Open CrossPath Assistant'}
                        className="h-14 w-14 rounded-2xl border-sky-200 !bg-sky-50 p-0 text-sky-700 shadow-[0_14px_30px_rgba(15,23,42,0.15)] ring-4 ring-white/85 hover:border-sky-300 hover:!bg-sky-100 hover:text-sky-800 hover:shadow-[0_18px_34px_rgba(15,23,42,0.20)]"
                    >
                        {isOpen ? <X size={22} className="text-slate-700" /> : <MessageCircle size={25} strokeWidth={1.9} />}
                    </Button>
                </Tooltip>
            </Motion>

            {isOpen && (
                <Motion
                    tag="div"
                    initial={{ opacity: 0, y: 20, scale: 0.94 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.9 }}
                    transition={{ duration: 0.24 }}
                    className="fixed bottom-24 right-6 z-50 flex h-[min(44rem,calc(100vh-9rem))] w-[min(34rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-[22px] border border-white bg-white/95 shadow-[0_28px_72px_rgba(51,65,85,0.18),0_8px_24px_rgba(100,116,139,0.12)] backdrop-blur-xl"
                >
                    <div className="h-1 bg-slate-300" />
                    <header className="flex items-center justify-between gap-5 border-b border-[#dce8f1] bg-[#f5f9fc] px-5 py-5 text-slate-800 shadow-[inset_0_-1px_0_rgba(255,255,255,0.8)]">
                        <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-[0_6px_16px_rgba(15,23,42,0.08)]">
                                <img src={CrossPathLogo} alt="" className="h-8 w-8 rounded-lg" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="text-base font-semibold tracking-normal">CrossPath Assistant</h3>
                                    <Badge color="slate" size="sm" className="border-[#cfe3da] bg-[#eef8f3] px-2 py-0.5 text-[#4f8066]">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#67a57d]" />
                                        Available
                                    </Badge>
                                </div>
                                <p className="mt-1 text-xs text-slate-500">Campaign, connection, and workflow guidance</p>
                            </div>
                        </div>
                        <Button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            variant="ghost"
                            size="xs"
                            aria-label="Close CrossPath Assistant"
                            className="h-9 w-9 rounded-xl border-[#d6e0e9] bg-white/70 p-0 text-slate-500 shadow-none hover:border-[#c2d1df] hover:bg-white hover:text-slate-700"
                        >
                            <X size={16} />
                        </Button>
                    </header>

                    <div className="flex-1 space-y-4 overflow-y-auto bg-[#f8fbfd] px-5 py-5">
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`max-w-[84%] rounded-2xl border px-4 py-3.5 shadow-sm ${
                                        message.sender === 'user'
                                            ? 'border-sky-200 bg-sky-50 text-slate-700 shadow-sky-100/80'
                                            : 'border-[#e3e9f0] bg-white text-slate-700 shadow-slate-200/60'
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        {message.sender === 'bot' && (
                                            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                                                <img src={CrossPathLogo} alt="" className="h-5 w-5 rounded-md" />
                                            </span>
                                        )}
                                        {message.sender === 'user' && (
                                            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#d9eaf5] text-[#4f789c]">
                                                <User size={16} />
                                            </span>
                                        )}
                                        <div className="flex-1">
                                            <p className="whitespace-pre-line text-sm leading-5">{message.text}</p>
                                            <p
                                                className={`mt-1.5 text-[11px] ${
                                                    message.sender === 'user' ? 'text-[#6f91ad]' : 'text-slate-400'
                                                }`}
                                            >
                                                {formatTime(message.timestamp)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {isTyping && (
                            <div className="flex justify-start">
                                <div className="rounded-2xl border border-slate-100 bg-white px-3.5 py-3 text-slate-700 shadow-sm">
                                    <div className="flex items-center gap-2">
                                        <MessageCircle size={15} className="text-[#6d879f]" />
                                        <div className="flex space-x-1">
                                            <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#b6cadc]" />
                                            <div
                                                className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#b6cadc]"
                                                style={{ animationDelay: '0.1s' }}
                                            />
                                            <div
                                                className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#b6cadc]"
                                                style={{ animationDelay: '0.2s' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <Separator className="bg-slate-200" />
                    <div className="bg-white px-5 py-4">
                        <p className="mb-3 px-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Popular questions</p>
                        <div className="grid grid-cols-2 gap-2">
                            {quickActions.map((action, index) => (
                                <Button
                                    type="button"
                                    key={index}
                                    onClick={() => handleQuickAction(action.action)}
                                    variant="ghost"
                                    size="xs"
                                className={`min-h-[42px] justify-start rounded-xl px-3.5 text-left text-xs shadow-none transition-all duration-200 ${quickActionStyles[index]}`}
                                >
                                    <action.icon size={15} className="shrink-0 opacity-80" />
                                    <span className="truncate">{action.label}</span>
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className="border-t border-slate-100 bg-[#f6f9fc] px-5 py-4">
                        <div className="flex gap-2">
                            <Input
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.currentTarget.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                placeholder="Ask about your workspace..."
                                variant="outline"
                                className="border-[#d4e2ec] py-2.5 text-sm shadow-sm focus:border-[#9ebbd2] focus:ring-[#e0edf6]"
                            />
                            <Button
                                type="button"
                                onClick={() => handleSendMessage()}
                                disabled={!inputValue.trim()}
                                variant="ghost"
                                size="sm"
                                aria-label="Send message"
                                className="h-11 w-11 rounded-xl border-emerald-300 !bg-emerald-500 p-0 text-white shadow-md shadow-emerald-100 hover:!bg-emerald-600 hover:text-white"
                            >
                                <Send size={16} />
                            </Button>
                        </div>
                    </div>
                </Motion>
            )}
        </>
    );
};

export default Chatbot;
